/**
 * Predicted waves from the Open-Meteo marine API — free, keyless, CORS-enabled,
 * and served in feet directly.
 *
 * NOAA has no browser-reachable wave forecast: WaveWatch III ships as GRIB via
 * NOMADS, and the NWS Coastal Waters Forecast carries seas only as prose
 * ("seas 10 to 15 ft"). This model tracked the offshore buoys within about a
 * foot when checked against live NDBC observations, and gives physically
 * sensible values inside the Bay — 3.7-4.3 ft at Fort Point where swell reaches
 * through the Gate against 1.4-1.7 ft off Alameda — rather than bleeding the
 * open-ocean value inshore.
 *
 * There is no grid cell for the South Bay or the Delta, so those stations get
 * nulls and no panel; that is the model being honest about its own resolution.
 */
export interface WavePoint {
  t: Date;
  /** Significant height of the combined sea, feet. */
  heightFt: number;
  /** Dominant period, seconds. */
  periodS: number | null;
  /** Direction the waves travel TOWARD, degrees true. */
  dirDeg: number | null;
  /** Swell component alone, feet — groundswell as distinct from local windsea. */
  swellFt: number | null;
}

const API = 'https://marine-api.open-meteo.com/v1/marine';
const FORECAST_HOURS = 48;

const cache = new Map<string, Promise<WavePoint[]>>();

/**
 * Predicted waves for one station over the next two days.
 *
 * Returns an empty array where the model has no water — the caller renders no
 * panel rather than an empty one.
 */
export function waveForecast(
  stationId: string,
  lat: number,
  lon: number,
): Promise<WavePoint[]> {
  const hit = cache.get(stationId);
  if (hit) return hit;

  const pending = fetchWaves(lat, lon).catch((err) => {
    cache.delete(stationId); // Let a transient failure be retried.
    throw err;
  });
  cache.set(stationId, pending);
  return pending;
}

async function fetchWaves(lat: number, lon: number): Promise<WavePoint[]> {
  const qs = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    hourly: 'wave_height,wave_period,wave_direction,swell_wave_height',
    length_unit: 'imperial',
    timezone: 'America/Los_Angeles',
    forecast_days: '3',
  });

  const res = await fetch(`${API}?${qs}`);
  if (!res.ok) throw new Error(`Waves HTTP ${res.status}`);

  const body = await res.json();
  if (body.error) throw new Error(String(body.reason ?? 'No wave forecast'));

  const h = body.hourly as {
    time: string[];
    wave_height: (number | null)[];
    wave_period: (number | null)[];
    wave_direction: (number | null)[];
    swell_wave_height: (number | null)[];
  } | undefined;
  if (!h?.time) return [];

  const now = Date.now();
  const out: WavePoint[] = [];

  for (let i = 0; i < h.time.length; i++) {
    const height = h.wave_height[i];
    if (height === null || !Number.isFinite(height)) continue;

    // Local wall-clock from the API, matching the display clock.
    const t = new Date(h.time[i]);
    const ahead = (t.getTime() - now) / 3_600_000;
    // A little history for context, then the forecast proper.
    if (ahead < -3 || ahead > FORECAST_HOURS) continue;

    out.push({
      t,
      heightFt: height,
      periodS: h.wave_period[i],
      dirDeg: h.wave_direction[i],
      swellFt: h.swell_wave_height[i],
    });
  }

  return out;
}
